import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  setDoc,
  startAfter,
  where,
  writeBatch,
} from 'firebase/firestore';
import { createUserInDatabaseIfDoesNotExist } from '../firebase';
import {
  collectDeckBookNumbers,
  deserializeDeckVerseChunks,
  serializeDeckVerseChunks,
} from './deck_storage_utils';
import { getTodayDateString, isValidDueDate } from './deck_pace_utils';

const READ_TIMEOUT_MS = 7000;
const READ_RETRIES = 3;
const MAX_DECK_CHUNKS = 32;
export const PUBLIC_DECK_PAGE_SIZE = 20;
const DEFAULT_MEMBER_NAME = 'Deck member';

export async function readDeckDataWithRetry(operation, retries = READ_RETRIES) {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      let timeoutId;
      try {
        return await Promise.race([
          operation(),
          new Promise((_, reject) => {
            timeoutId = setTimeout(
              () => reject(new Error('Deck request timed out')),
              READ_TIMEOUT_MS
            );
          }),
        ]);
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        await new Promise(resolve => {
          setTimeout(resolve, 1000 * (2 ** attempt));
        });
      }
    }
  }
  throw lastError;
}

export function validatePace(pace) {
  if (
    !pace
    || typeof pace.tier !== 'string'
    || !Number.isInteger(pace.verses_per_day)
    || pace.verses_per_day < 1
    || (
      pace.minutes_per_day !== null
      && (!Number.isInteger(pace.minutes_per_day) || pace.minutes_per_day < 1)
    )
  ) {
    throw new TypeError('Deck pace is invalid');
  }
}

function validateIdentity(database, uid) {
  if (!database) {
    throw new TypeError('Firestore database is required');
  }
  if (typeof uid !== 'string' || !uid) {
    throw new TypeError('A signed-in user is required');
  }
}

function normalizeDisplayName(displayName) {
  return typeof displayName === 'string' && displayName.trim()
    ? displayName.trim().slice(0, 60)
    : DEFAULT_MEMBER_NAME;
}

function createMembership(role = 'member', displayName = '') {
  const timestamp = Date.now();
  return {
    role,
    joined_at: timestamp,
    display_name: normalizeDisplayName(displayName),
    learned_count: 0,
    retained_count: 0,
    progress_revision: 0,
    progress_updated_at: timestamp,
  };
}

export async function loadDeck(database, deckId) {
  if (!database || !deckId) {
    throw new TypeError('A database and deck ID are required');
  }
  const [deckSnapshot, chunksSnapshot] = await readDeckDataWithRetry(() => Promise.all([
    getDoc(doc(database, 'decks', deckId)),
    getDocs(query(
      collection(database, 'decks', deckId, 'verse_chunks'),
      orderBy('order')
    )),
  ]));
  if (!deckSnapshot.exists()) {
    return null;
  }
  const chunks = chunksSnapshot.docs.map(chunkSnapshot => ({
    id: chunkSnapshot.id,
    ...chunkSnapshot.data(),
  }));
  return {
    id: deckSnapshot.id,
    ...deckSnapshot.data(),
    chunks,
    verses: deserializeDeckVerseChunks(chunks),
  };
}

export async function checkDeckMembership(database, uid, deckId) {
  validateIdentity(database, uid);
  if (!deckId) {
    throw new TypeError('A deck ID is required');
  }
  const snapshot = await readDeckDataWithRetry(
    () => getDoc(doc(database, 'decks', deckId, 'users', uid))
  );
  return snapshot.exists() ? { id: uid, ...snapshot.data() } : null;
}

export async function joinDeck(database, user, deckId) {
  validateIdentity(database, user?.uid);
  if (!deckId) {
    throw new TypeError('A deck ID is required');
  }
  const existingMembership = await readDeckDataWithRetry(
    () => getDoc(doc(database, 'decks', deckId, 'users', user.uid))
  );
  if (existingMembership.exists()) {
    return { id: user.uid, ...existingMembership.data() };
  }
  await createUserInDatabaseIfDoesNotExist(database, user);
  const membership = createMembership('member', user.displayName);
  const batch = writeBatch(database);
  batch.set(doc(database, 'decks', deckId, 'users', user.uid), membership);
  batch.set(doc(database, 'users', user.uid, 'decks', deckId), membership);
  await batch.commit();
  return membership;
}

export async function leaveDeck(database, uid, deckId) {
  validateIdentity(database, uid);
  const batch = writeBatch(database);
  batch.delete(doc(database, 'decks', deckId, 'users', uid));
  batch.delete(doc(database, 'users', uid, 'decks', deckId));
  await batch.commit();
}

export async function archiveUserDeck(database, uid, deckId, archived = true) {
  validateIdentity(database, uid);
  await setDoc(
    doc(database, 'users', uid, 'decks', deckId),
    { archived },
    { merge: true }
  );
}

export async function syncDeckMemberDisplayName(
  database,
  uid,
  deckId,
  membership,
  displayName
) {
  validateIdentity(database, uid);
  const nextDisplayName = normalizeDisplayName(displayName);
  if (!membership || membership.display_name === nextDisplayName) {
    return null;
  }
  const batch = writeBatch(database);
  batch.update(
    doc(database, 'decks', deckId, 'users', uid),
    { display_name: nextDisplayName }
  );
  batch.update(
    doc(database, 'users', uid, 'decks', deckId),
    { display_name: nextDisplayName }
  );
  await batch.commit();
  return nextDisplayName;
}

export async function countArchivedUserDecks(database, uid) {
  validateIdentity(database, uid);
  const snapshot = await readDeckDataWithRetry(() => getDocs(query(
    collection(database, 'users', uid, 'decks'),
    where('archived', '==', true)
  )));
  return snapshot.size;
}

export async function loadDeckMembers(database, deckId) {
  if (!database || !deckId) {
    throw new TypeError('A database and deck ID are required');
  }
  const membersSnapshot = await readDeckDataWithRetry(
    () => getDocs(collection(database, 'decks', deckId, 'users'))
  );
  return membersSnapshot.docs.map(memberSnapshot => {
    const membership = memberSnapshot.data();
    return {
      id: memberSnapshot.id,
      ...membership,
      displayName: normalizeDisplayName(membership.display_name),
    };
  });
}

async function updateDeckMetadata(database, deckId, fields) {
  await setDoc(doc(database, 'decks', deckId), fields, { merge: true });
}

export function renameDeck(database, deckId, deckName) {
  if (typeof deckName !== 'string' || !deckName.trim()) {
    throw new TypeError('Deck name is required');
  }
  return updateDeckMetadata(database, deckId, { deck_name: deckName.trim() });
}

export function updateDeckVisibility(database, deckId, isPublic) {
  if (typeof isPublic !== 'boolean') {
    throw new TypeError('Deck visibility must be a boolean');
  }
  return updateDeckMetadata(database, deckId, { is_public: isPublic });
}

export function updateDeckPace(database, deckId, pace, dueDate = null) {
  validatePace(pace);
  if (dueDate !== null && !isValidDueDate(dueDate, getTodayDateString())) {
    throw new RangeError('Due date cannot be before today');
  }
  return updateDeckMetadata(database, deckId, {
    pace: { ...pace },
    due_date: dueDate || null,
  });
}

export async function updateDeckAdminRole(database, deckId, member, makeAdmin) {
  validateIdentity(database, member?.id);
  if (typeof makeAdmin !== 'boolean') {
    throw new TypeError('Admin state must be a boolean');
  }
  const role = makeAdmin ? 'admin' : 'member';
  const batch = writeBatch(database);
  batch.update(doc(database, 'decks', deckId, 'users', member.id), { role });
  batch.update(doc(database, 'users', member.id, 'decks', deckId), { role });
  await batch.commit();
}

export async function removeDeckMember(database, deckId, memberId) {
  validateIdentity(database, memberId);
  const batch = writeBatch(database);
  batch.delete(doc(database, 'decks', deckId, 'users', memberId));
  batch.delete(doc(database, 'users', memberId, 'decks', deckId));
  await batch.commit();
}

export async function rewriteDeckVerses(database, deckId, verses) {
  const chunks = serializeDeckVerseChunks(verses);
  if (chunks.length === 0) {
    throw new RangeError('A deck must contain at least one verse');
  }
  if (chunks.length > MAX_DECK_CHUNKS) {
    throw new RangeError(`A deck cannot exceed ${MAX_DECK_CHUNKS} verse chunks`);
  }
  const deckReference = doc(database, 'decks', deckId);
  await runTransaction(database, async transaction => {
    const deckSnapshot = await transaction.get(deckReference);
    if (!deckSnapshot.exists()) {
      throw new RangeError('Deck not found');
    }
    const currentChunkCount = deckSnapshot.data().chunk_count;
    if (!Number.isInteger(currentChunkCount) || currentChunkCount < 0) {
      throw new TypeError('Deck chunk count is invalid');
    }
    chunks.forEach(({ id, ...chunk }) => {
      transaction.set(doc(database, 'decks', deckId, 'verse_chunks', id), chunk);
    });
    for (let index = chunks.length; index < currentChunkCount; index += 1) {
      transaction.delete(doc(
        database,
        'decks',
        deckId,
        'verse_chunks',
        String(index).padStart(4, '0')
      ));
    }
    transaction.set(deckReference, {
      verse_count: chunks.reduce((total, chunk) => total + chunk.verse_count, 0),
      chunk_count: chunks.length,
      book_numbers: collectDeckBookNumbers(verses),
      content_revision: (deckSnapshot.data().content_revision || 0) + 1,
    }, { merge: true });
  });
}

export async function loadPublicDecks(database, uid = null, options = {}) {
  if (!database) {
    throw new TypeError('Firestore database is required');
  }
  const { pageSize = PUBLIC_DECK_PAGE_SIZE, startAfterDeck = null } = options;
  if (!Number.isInteger(pageSize) || pageSize < 1) {
    throw new RangeError('Deck page size must be a positive integer');
  }
  const constraints = [
    where('is_public', '==', true),
    orderBy('participant_count', 'desc'),
    orderBy('created_at', 'desc'),
  ];
  if (startAfterDeck) {
    constraints.push(startAfter(
      startAfterDeck.participant_count || 0,
      startAfterDeck.created_at || 0
    ));
  }
  const decksSnapshot = await readDeckDataWithRetry(() => getDocs(query(
    collection(database, 'decks'),
    ...constraints,
    limit(pageSize)
  )));
  const joinedIds = new Set();
  if (uid) {
    const joinedSnapshot = await readDeckDataWithRetry(
      () => getDocs(collection(database, 'users', uid, 'decks'))
    );
    joinedSnapshot.docs.forEach(deckSnapshot => joinedIds.add(deckSnapshot.id));
  }
  return decksSnapshot.docs.map(deckSnapshot => {
    const data = deckSnapshot.data();
    return {
      id: deckSnapshot.id,
      ...data,
      participantCount: Number.isInteger(data.participant_count)
        ? data.participant_count
        : null,
      joined: joinedIds.has(deckSnapshot.id),
    };
  });
}

export async function loadUserDecks(database, uid, options = {}) {
  validateIdentity(database, uid);
  const { archived = false } = options;
  const userDecksSnapshot = await readDeckDataWithRetry(
    () => getDocs(collection(database, 'users', uid, 'decks'))
  );
  const matchingDecks = userDecksSnapshot.docs.filter(userDeckSnapshot => (
    (userDeckSnapshot.data().archived === true) === archived
  ));
  const decks = await Promise.all(matchingDecks.map(async userDeckSnapshot => {
    const deckSnapshot = await readDeckDataWithRetry(
      () => getDoc(doc(database, 'decks', userDeckSnapshot.id))
    );
    if (!deckSnapshot.exists()) {
      return null;
    }
    return {
      id: userDeckSnapshot.id,
      ...deckSnapshot.data(),
      ...userDeckSnapshot.data(),
    };
  }));
  return decks.filter(Boolean);
}

export async function createDeckInDatabase(database, user, {
  deckName,
  isPublic,
  dueDate = null,
  pace,
  verses,
}) {
  if (!database) {
    throw new TypeError('Firestore database is required');
  }
  if (!user?.uid) {
    throw new TypeError('A signed-in user is required');
  }
  if (typeof deckName !== 'string' || !deckName.trim()) {
    throw new TypeError('Deck name is required');
  }
  if (typeof isPublic !== 'boolean') {
    throw new TypeError('Deck visibility must be a boolean');
  }
  if (dueDate !== null && !isValidDueDate(dueDate, getTodayDateString())) {
    throw new RangeError('Due date cannot be before today');
  }
  validatePace(pace);

  const chunks = serializeDeckVerseChunks(verses);
  if (chunks.length === 0) {
    throw new RangeError('A deck must contain at least one verse');
  }

  const deckRef = doc(collection(database, 'decks'));
  await createUserInDatabaseIfDoesNotExist(database, user);

  const timestamp = Date.now();
  const membership = createMembership('admin', user.displayName);
  const batch = writeBatch(database);

  batch.set(deckRef, {
    deck_name: deckName.trim(),
    is_public: isPublic,
    due_date: dueDate || null,
    pace: { ...pace },
    verse_count: chunks.reduce((total, chunk) => total + chunk.verse_count, 0),
    chunk_count: chunks.length,
    book_numbers: collectDeckBookNumbers(verses),
    content_revision: 1,
    participant_count: 0,
    created_by: user.uid,
    created_at: timestamp,
  });

  chunks.forEach(({ id, ...chunk }) => {
    batch.set(doc(database, 'decks', deckRef.id, 'verse_chunks', id), chunk);
  });

  batch.set(doc(database, 'decks', deckRef.id, 'users', user.uid), membership);
  batch.set(doc(database, 'users', user.uid, 'decks', deckRef.id), membership);
  await batch.commit();

  return deckRef.id;
}
